import { useState, useEffect } from 'react';
import { Play, Pause, Heart, Headphones, Music } from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useFan } from '../contexts/FanContext';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Mixes = () => {
  const [mixes, setMixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playMix, currentMix, isPlaying, togglePlay, formatTime } = usePlayer();
  const { fan, toggleFavorite, isFavorite } = useFan();

  useEffect(() => {
    fetchMixes();
  }, []);

  const fetchMixes = async () => {
    try {
      const response = await axios.get(`${API_URL}/mixes`);
      setMixes(response.data);
    } catch (error) {
      console.error('Failed to fetch mixes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (mix) => {
    if (currentMix?.id === mix.id) {
      togglePlay();
    } else {
      playMix(mix, mixes);
    }
  };

  const handleFavorite = async (mixId) => {
    if (!fan) {
      toast.error('Join to save favorites!');
      return;
    }
    await toggleFavorite(mixId);
  };

  return (
    <div className="min-h-screen py-20 px-6" data-testid="mixes-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            All <span className="gradient-text">Mixes</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Exclusive sets and mixes from DJ Shadow
          </p>
        </div>

        {/* Mixes Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl bg-zinc-800/50" />
            ))}
          </div>
        ) : mixes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mixes.map((mix) => {
              const isCurrentMix = currentMix?.id === mix.id;
              const isThisPlaying = isCurrentMix && isPlaying;

              return (
                <div
                  key={mix.id}
                  className={`glass-card rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
                    isCurrentMix ? 'border-[#D9F99D]/50' : 'hover:border-white/20'
                  }`}
                  data-testid={`mix-item-${mix.id}`}
                  onClick={() => handlePlay(mix)}
                >
                  <div className="flex gap-4 p-4">
                    {/* Cover */}
                    <div
                      className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 relative group bg-zinc-900"
                    >
                      {mix.cover_url ? (
                        <img 
                          src={mix.cover_url} 
                          alt={mix.title}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#27272A] to-[#18181B] flex items-center justify-center">
                          <Headphones className="w-10 h-10 text-zinc-700" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {isThisPlaying ? (
                          <Pause className="w-8 h-8 text-white" />
                        ) : (
                          <Play className="w-8 h-8 text-white ml-1" />
                        )}
                      </div>
                      {isThisPlaying && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="flex items-end gap-0.5 h-8">
                            {[...Array(4)].map((_, i) => (
                              <div key={i} className="w-1 bg-[#D9F99D] rounded-full audio-bar" style={{ height: '100%' }} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <h3 className={`font-bold text-lg truncate ${isCurrentMix ? 'text-[#D9F99D]' : ''}`}>
                          {mix.title}
                        </h3>
                        <p className="text-zinc-400 text-sm">{mix.genre || 'Mix'}</p>
                        {mix.description && (
                          <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{mix.description}</p>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span>{mix.plays_count || 0} plays</span>
                          {mix.duration && <span>{formatTime(mix.duration)}</span>}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFavorite(mix.id);
                          }}
                          className="p-2 hover:bg-white/5 rounded-full transition-colors"
                        >
                          <Heart 
                            className={`w-4 h-4 ${
                              isFavorite(mix.id) ? 'fill-[#F472B6] text-[#F472B6]' : 'text-zinc-400'
                            }`} 
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="glass-card rounded-2xl p-16 text-center">
            <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No mixes yet</h3>
            <p className="text-zinc-400">Check back soon for new releases!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Mixes;
